import { UHttp } from '../../utils/UHttp';

const API_URL = process.env.REACT_APP_URL_SERVER;

// Types
export type PlanName = 'junior' | 'premium' | 'premium_plus';
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete';

export interface Plan {
  id: PlanName;
  name: string;
  price: number;
  period: string;
  description: string;
  stripePriceId?: string;
  features: {
    maxCompanies: number;
    maxCollaborators: number;
    maxActivePromoCodes: number;
    onlinePayment: boolean;
    depositPayment: boolean;
    reviewSystem: boolean;
    bookingReminders: boolean;
    advancedAnalytics: boolean;
    prioritySupport: boolean;
  };
}

export interface Subscription {
  id: string;
  ownerId: string;
  planName: PlanName;
  planPrice: number;
  status: SubscriptionStatus;
  stripeSubscriptionId: string;
  stripePriceId: string;
  stripeCustomerId: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  trialEnd?: string;

  // Feature flags
  maxCompanies: number;
  maxCollaborators: number;
  maxActivePromoCodes: number;
  onlinePayment: boolean;
  depositPayment: boolean;
  reviewSystem: boolean;
  bookingReminders: boolean;
  advancedAnalytics: boolean;
  prioritySupport: boolean;

  createdAt: string;
  updatedAt: string;
}

export interface CheckoutSessionResponse {
  success: boolean;
  message: string;
  url?: string;
  sessionId?: string;
}

export interface SubscriptionResponse {
  success: boolean;
  message: string;
  data?: Subscription;
}

export interface PlansResponse {
  success: boolean;
  message: string;
  data?: Plan[];
}

export interface LimitCheckResponse {
  success: boolean;
  message: string;
  data?: {
    canCreate: boolean;
    currentCount: number;
    limit: number;
  };
}

export class SubscriptionModel {
  private static instance: SubscriptionModel;

  private constructor() {}

  public static getInstance(): SubscriptionModel {
    if (!SubscriptionModel.instance) {
      SubscriptionModel.instance = new SubscriptionModel();
    }
    return SubscriptionModel.instance;
  }

  /**
   * Récupère tous les plans disponibles
   */
  async getPlans(): Promise<PlansResponse> {
    try {
      const response = await UHttp.get(
        `${API_URL}/api/subscription/plans`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la récupération des plans'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Plans récupérés avec succès',
        data: data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Récupère l'abonnement actuel de l'utilisateur
   */
  async getCurrentSubscription(): Promise<SubscriptionResponse> {
    try {
      const response = await UHttp.get(
        `${API_URL}/api/subscription/current`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la récupération de l\'abonnement'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Abonnement récupéré avec succès',
        data: data.data // Le backend retourne { success: true, data: subscription }
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Crée une session de checkout Stripe
   */
  async createCheckoutSession(planId: PlanName): Promise<CheckoutSessionResponse> {
    try {
      const response = await UHttp.post(
        `${API_URL}/api/subscription/create-checkout-session`,
        { planId },
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la création de la session de paiement'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Session créée avec succès',
        url: data.url,
        sessionId: data.sessionId
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Change de plan d'abonnement
   */
  async changePlan(newPlanId: PlanName): Promise<SubscriptionResponse> {
    try {
      const response = await UHttp.post(
        `${API_URL}/api/subscription/change-plan`,
        { planId: newPlanId },
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors du changement de plan'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Plan modifié avec succès',
        data: data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Annule l'abonnement (à la fin de la période en cours)
   */
  async cancelSubscription(): Promise<SubscriptionResponse> {
    try {
      const response = await UHttp.post(
        `${API_URL}/api/subscription/cancel`,
        {},
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de l\'annulation de l\'abonnement'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Abonnement annulé avec succès',
        data: data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Réactive un abonnement annulé
   */
  async reactivateSubscription(): Promise<SubscriptionResponse> {
    try {
      const response = await UHttp.post(
        `${API_URL}/api/subscription/reactivate`,
        {},
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la réactivation de l\'abonnement'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Abonnement réactivé avec succès',
        data: data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Crée une session pour accéder au portail de facturation Stripe
   */
  async createBillingPortalSession(): Promise<{ success: boolean; message: string; url?: string }> {
    try {
      const response = await UHttp.post(
        `${API_URL}/api/subscription/billing-portal`,
        {},
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la création de la session du portail'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Session créée avec succès',
        url: data.url
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Vérifie si l'utilisateur peut créer une company
   */
  async canCreateCompany(): Promise<LimitCheckResponse> {
    try {
      const response = await UHttp.get(
        `${API_URL}/api/subscription/limits/can-create-company`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la vérification de la limite'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Vérification effectuée',
        data: data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Vérifie si l'utilisateur peut créer un collaborateur
   */
  async canCreateCollaborator(): Promise<LimitCheckResponse> {
    try {
      const response = await UHttp.get(
        `${API_URL}/api/subscription/limits/can-create-collaborator`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la vérification de la limite'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Vérification effectuée',
        data: data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

}
